import type { Drug } from '@/api/domains/drugs.types';
import { NamesByRecordStatusId, RECORD_STATUS_UNKNOWN } from '@/shared/constants/record-status.constants';
import { formatMakerName } from '@/shared/helpers/format.helpers';
import { compareIds, compareString } from '@/shared/helpers/grid-filter.helpers';

import { createSortFunction, type TFilterItem } from '../ReactDataGrid';
import type { NomenclatureDataGridRowData } from './types';

export const prepareSearchQueryFromDrugName = (drugName: string) => {
  const [firstWord, secondWord] = drugName.split(' ');

  if (firstWord.length >= 3 || !secondWord) {
    return firstWord;
  }

  return `${firstWord} ${secondWord}`;
};

export const prepareNomenclatureBarcodes = (barcodes: string[]) => {
  return barcodes.map(value => ({ value }));
};

export type NomenclaturesFilterData = {
  idsFilterData: TFilterItem[];
  categoriesFilterData: TFilterItem[];
  drugFullNameFilterData: Array<TFilterItem<string>>;
  makerFilterData: TFilterItem[];
  statusFilterData: TFilterItem[];
  licenseHolderFilterData: TFilterItem[];
  initiatorFilterData: TFilterItem[];
};

type GetFilterDataParams = {
  items: NomenclatureDataGridRowData[];
};

export const initialNomenclaturesFilterData: NomenclaturesFilterData = {
  idsFilterData: [],
  categoriesFilterData: [],
  drugFullNameFilterData: [],
  makerFilterData: [],
  initiatorFilterData: [],
  statusFilterData: [],
  licenseHolderFilterData: [],
};

export const getNomenclatureDataGridRows = (drugs: Drug[]): NomenclatureDataGridRowData[] => {
  const dataGridRows = drugs.map(nomenclature => {
    const { id, category, maker, updated_at, initiator, record_status_id, license_holder } = nomenclature;

    return {
      id,
      drug: nomenclature,
      maker,
      recordStatus: record_status_id,
      category,
      updateDate: new Date(updated_at),
      initiator,
      licenseHolder: license_holder,
    };
  });

  return dataGridRows;
};

export const getFilterData = ({ items }: GetFilterDataParams): NomenclaturesFilterData => {
  const idsFilterData: TFilterItem[] = [];

  const drugFullNameFilterMapData: Record<string, boolean> = {};
  const drugFullNameFilterData: Array<TFilterItem<string>> = [];

  const makerFilterMapData: Record<number, boolean> = {};
  const makerFilterData: TFilterItem[] = [];

  const licenseHolderFilterMapData: Record<number, boolean> = {};
  const licenseHolderFilterData: TFilterItem[] = [];

  const statusFilterMapData: Record<number, boolean> = {};
  const statusFilterData: TFilterItem[] = [];

  const categoriesFilterMapData: Record<number, boolean> = {};
  const categoriesFilterData: TFilterItem[] = [];

  const initiatorFilterMapData: Record<number, boolean> = {};
  const initiatorFilterData: TFilterItem[] = [];

  items.forEach(nomenclature => {
    const { id, category, drug: originDrug, maker, initiator, recordStatus, licenseHolder } = nomenclature;
    const { full_name } = originDrug;

    idsFilterData.push({ id, label: String(id) });

    if (full_name && !drugFullNameFilterMapData[full_name]) {
      drugFullNameFilterMapData[full_name] = true;
      drugFullNameFilterData.push({ id: full_name, label: full_name });
    }

    if (maker && !makerFilterMapData[maker.id]) {
      makerFilterMapData[maker.id] = true;
      makerFilterData.push({
        id: maker.id,
        label: formatMakerName(maker.name, maker.country_name),
      });
    }

    if (licenseHolder && !licenseHolderFilterMapData[licenseHolder.id]) {
      licenseHolderFilterMapData[licenseHolder.id] = true;
      licenseHolderFilterData.push({ id: licenseHolder.id, label: licenseHolder.name });
    }

    if (!statusFilterMapData[recordStatus]) {
      statusFilterMapData[recordStatus] = true;
      statusFilterData.push({
        id: recordStatus,
        label: NamesByRecordStatusId[recordStatus] || RECORD_STATUS_UNKNOWN,
      });
    }

    if (category && !categoriesFilterMapData[category.id]) {
      categoriesFilterMapData[category.id] = true;
      categoriesFilterData.push({ id: category.id, label: category.name });
    }

    if (initiator && !initiatorFilterMapData[initiator.id]) {
      initiatorFilterMapData[initiator.id] = true;
      initiatorFilterData.push({ id: initiator.id, label: initiator.name });
    }
  });

  const filterData = {
    idsFilterData: idsFilterData.sort(compareIds),
    drugFullNameFilterData: drugFullNameFilterData.sort(compareString),
    makerFilterData: makerFilterData.sort(compareString),
    statusFilterData: statusFilterData.sort(compareString),
    categoriesFilterData: categoriesFilterData.sort(compareString),
    initiatorFilterData: initiatorFilterData.sort(compareString),
    licenseHolderFilterData: licenseHolderFilterData.sort(compareString),
  };

  return filterData;
};

export const sortNomenclature = createSortFunction<NomenclatureDataGridRowData>({
  id: sortBy => sortBy.number(id => id),
  category: sortBy => sortBy.string(category => category?.name),
  drug: sortBy => sortBy.string(drug => drug.full_name),
  maker: sortBy => sortBy.string(maker => maker?.name),
  updateDate: sortBy => sortBy.date(updateDate => updateDate),
  initiator: sortBy => sortBy.string(initiator => initiator?.name),
  recordStatus: sortBy => sortBy.number(recordStatusId => recordStatusId),
  licenseHolder: sortBy => sortBy.string(licenseHolder => licenseHolder?.name),
});
